import PropTypes from 'prop-types';
import React, { Children, useCallback, useMemo, memo } from 'react';
import { List, Map, fromJS } from 'immutable';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import AutoComplete from '../AutoComplete';
import DeepValue from '../../decorators/DeepValue';
import ChangeDetection from '../../decorators/ChangeDetection';

/**
 * Dropdown wrapper.
 *
 * @param props {Object}.
 * @param [props.wrapperClassName] {string} Class names for main div. `"dropdown"` class
 *   is already added to element. Need not to pass it.
 * @param [props.className] {string} `<button>` class name. `"btn dropdown-toggle"` classes
 *    are already added to element. Need not to pass them.
 * @param [props.value=""] {string} Current value of dropdown. Hidden input has this value.
 * @param [props.name] {string} Name attribute for input.
 * @param [props.children] {Element[]} DropDown's content in XML format.
 * @param [props.options] {Immutable.List} DropDown's content in js format.
 * @param [props.className] {String} Extra classNames for button tag of DD.
 * @param [props.onChange] {Function} OnDropDown change callback (need change value manual).
 * @param [props.allowEmptyValue] {Boolean} Allow set empty value on blur.
 */
const DropDown = props => {
  const {
    name,
    disabled = false,
    changed = false,
    children = [],
    options = [],
    value,
    allowEmptyValue,
    onChange,
    wrapperClassName: wrapperClassNameProp,
    className,
  } = props;

  const computedOptions = useMemo(() => {
    let collectedOptions = List();

    Children.forEach(children, child => {
      collectedOptions = collectedOptions.push(fromJS(child.props));
    });
    options.forEach(p => {
      collectedOptions = collectedOptions.push(p);
    });

    return collectedOptions;
  }, [children, options]);

  const getOptions = useCallback(
    searchValue => {
      const regExp = new RegExp(String(searchValue).replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1'), 'i');

      return computedOptions.filter(option => regExp.test(option.get('value')));
    },
    [computedOptions],
  );

  const handleBlur = useCallback(
    event => {
      if (!onChange) return;

      if (value === undefined) return;
      if (value === 0) return;

      const regExp = new RegExp(`^${String(value).replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}$`, 'i');
      const firstValue = allowEmptyValue ? List() : options.get(0);
      const nextValue = computedOptions.find(option => regExp.test(option.get('value')), null, firstValue);

      event.target.name = name;
      event.target.value = nextValue.get('value') || '';

      return onChange(event);
    },
    [onChange, value, allowEmptyValue, options, computedOptions, name],
  );

  const handleSelect = useCallback(
    (event, suggestion) => {
      if (!onChange) return;

      const { suggestionValue: selectedValue } = suggestion;

      if (selectedValue === undefined) return;
      if (selectedValue === 0) return;
      event.target.name = name;
      event.target.value = selectedValue;

      return onChange(event);
    },
    [onChange, name],
  );

  const handleRenderOption = useCallback(option => <div>{option.name || option.value}</div>, []);

  const wrapperClassName = classNames('dropdown', wrapperClassNameProp);

  const inputProps = {
    name,
    disabled,
    className: classNames('form-control input-sm', className, {
      changed,
    }),
  };

  const values = getValues(props);

  return (
    <div className={wrapperClassName}>
      <input name={name} type="hidden" value={values.value} />
      <AutoComplete
        canEverBeLoading={false}
        checkOn="text"
        formGroupClass=" "
        getSuggestion={suggestion => suggestion?.value ?? ''}
        getSuggestionValue={suggestion => (suggestion && suggestion.value) || suggestion}
        inputProps={{ ...inputProps, onBlur: handleBlur, autoComplete: 'off' }}
        loading={false}
        minSuggestLength={0}
        name={name}
        onSuggestionSelected={handleSelect}
        renderSuggestion={handleRenderOption}
        suggestions={getOptions(value)}
        text={String(value)}
        suggestIfEmpty
      />
      <span
        className="caret"
        style={{
          position: 'absolute',
          right: 5,
          bottom: '40%',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
};

DropDown.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  disabled: PropTypes.bool, // oneOfType([PropTypes.string, PropTypes.instanceOf(Map)]),
  name: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.instanceOf(List),
  value: PropTypes.any,
  wrapperClassName: PropTypes.string,
};

/**
 * DropDown element row. Wraps content by `<li><a> ... </a></li>`.
 *
 * @param props {Object} Attributes for `<a>` tag in element.
 * @param props.value {String} Value of this dropdown element.
 * @param [props.type="anchor"] {String} One of "anchor" or "link". If "anchor" - use `<a/>`
 *    If "link" - use ReactRouter's Link element.
 * For example `{ onClick:()=>{} }`.
 */
const DropDownElement = props => {
  const { value, children = value, type = 'anchor', ...rest } = props;
  const { className: appendClassName, ...properties } = rest;
  const className = classNames('min-height', appendClassName);

  let content;

  switch (type) {
    case 'link':
      content = (
        <Link className={className} {...properties}>
          {children}
        </Link>
      );
      break;

    case 'anchor':
      content = (
        <a className={className} {...properties}>
          {children}
        </a>
      );
      break;

    default:
      break;
  }

  return <li>{content}</li>;
};

DropDownElement.propTypes = {
  allowEmptyValue: PropTypes.bool,
  children: PropTypes.node,
  option: PropTypes.instanceOf(Map),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Map)]),
};

export { DropDownElement };

export default DeepValue(ChangeDetection(memo(DropDown)));

function getValues(props) {
  const result = {};

  if ('defaultValue' in props) {
    result.defaultValue = props.defaultValue;
  }

  if ('buttonContent' in props) {
    result.buttonContent = props.buttonContent;
    result.value = props.buttonContent;
  }

  if ('value' in props) {
    result.value = props.value;
  }

  if (Map.isMap(result.defaultValue)) {
    result.buttonContent = result.defaultValue.get('name');
    result.value = result.defaultValue.get('value');
  }

  if (Map.isMap(result.value)) {
    result.buttonContent = result.value.get('name');
    result.value = result.value.get('value');
  }

  return result;
}
